import React, { useState, useEffect } from 'react'
import { confirmCallTimesheet } from '../services/telephonyApi'

/**
 * PostCallTimesheetModal — Gap 5
 * Shown to the agent after a call ends to confirm the auto-created timesheet.
 * Displays matched project, call duration, and lets agent enrich with call notes.
 * On confirm: PATCH /api/telephony/call-timesheet/{timesheetId}
 */
export default function PostCallTimesheetModal({
  isOpen,
  onClose,
  timesheetId,
  callSid,
  projectName,
  durationSeconds,
  callerName,
  callerNumber,
  direction = 'INBOUND',
  initialNotes = '',
}) {
  const [callNotes, setCallNotes] = useState(initialNotes || '')
  const [submitting, setSubmitting] = useState(false)
  const [confirmed, setConfirmed] = useState(false)

  useEffect(() => {
    if (isOpen) {
      setCallNotes(initialNotes || '')
      setConfirmed(false)
    }
  }, [isOpen, initialNotes])

  if (!isOpen) return null

  const formatDuration = (secs) => {
    if (!secs) return '0:00'
    const h = Math.floor(secs / 3600)
    const m = Math.floor((secs % 3600) / 60)
    const s = secs % 60
    if (h > 0) return `${h}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`
    return `${m}:${s.toString().padStart(2, '0')}`
  }

  const decimalHours = durationSeconds ? (durationSeconds / 3600).toFixed(2) : '0.00'

  const handleConfirm = async () => {
    const targetId = timesheetId || callSid
    if (!targetId) {
      // No timesheet yet (unknown caller without project match) — just close
      onClose()
      return
    }
    setSubmitting(true)
    try {
      await confirmCallTimesheet(targetId, callNotes)
      setConfirmed(true)
      setTimeout(onClose, 1500)
    } catch (err) {
      console.error('Timesheet confirmation failed:', err)
      setSubmitting(false)
    }
  }

  return (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
      <div className="w-full max-w-md bg-surface-container-lowest rounded-2xl shadow-2xl border border-outline-variant/30 overflow-hidden animate-slide-in-right">

        {/* Header */}
        <div className="bg-gradient-to-r from-primary to-primary-container text-on-primary p-5 flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
            <span className="material-symbols-outlined text-xl">timer</span>
          </div>
          <div>
            <h3 className="font-display font-bold text-base">Call Completed</h3>
            <p className="text-xs text-blue-100">Auto-timesheet created — please review &amp; confirm</p>
          </div>
        </div>

        {/* Content */}
        {confirmed ? (
          <div className="p-8 text-center space-y-3">
            <span className="material-symbols-outlined text-4xl text-amber-600">hourglass_top</span>
            <p className="font-bold text-on-surface">Timesheet Confirmed &amp; Submitted!</p>
            <p className="text-xs text-on-surface-variant">Your call notes are saved. The timesheet is in PENDING status and has been submitted to your manager's approval inbox.</p>
          </div>
        ) : (
          <div className="p-5 space-y-4">
            {/* Call summary */}
            <div className="p-4 rounded-xl bg-surface-container-low border border-outline-variant/20 space-y-2">
              <div className="flex items-center justify-between">
                <span className={`px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-wider ${
                  direction.toUpperCase() === 'OUTBOUND'
                    ? 'bg-blue-100 text-blue-800'
                    : 'bg-emerald-100 text-emerald-800'
                }`}>
                  {direction} Call
                </span>
                <span className="text-xs font-bold text-on-surface font-display">
                  {formatDuration(durationSeconds)}
                </span>
              </div>

              <div className="grid grid-cols-2 gap-3 pt-1">
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-wider text-outline">Contact</p>
                  <p className="text-sm font-semibold text-on-surface">{callerName || callerNumber || 'Unknown'}</p>
                  {callerName && <p className="text-xs text-on-surface-variant">{callerNumber}</p>}
                </div>
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-wider text-outline">Project</p>
                  <p className="text-sm font-semibold text-on-surface">{projectName || 'Unassigned'}</p>
                </div>
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-wider text-outline">Time Logged</p>
                  <p className="text-sm font-semibold text-on-surface">{decimalHours} hrs</p>
                </div>
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-wider text-outline">Status</p>
                  <p className="text-xs font-bold text-amber-700 bg-amber-50 px-2 py-0.5 rounded border border-amber-200 inline-block">PENDING (Manager Approval)</p>
                </div>
              </div>
            </div>

            {/* Call notes */}
            <div className="space-y-1.5">
              <label className="text-xs font-bold text-on-surface uppercase tracking-wider block">
                Call Notes <span className="text-outline font-normal normal-case">(auto-saves &amp; enriches timesheet)</span>
              </label>
              <textarea
                value={callNotes}
                onChange={(e) => setCallNotes(e.target.value)}
                rows={3}
                placeholder="Summarize the call, decisions made, action items..."
                className="w-full p-3 rounded-xl border border-outline-variant/40 bg-surface-container-lowest text-xs text-on-surface focus:ring-2 focus:ring-primary focus:outline-none transition-all"
              />
            </div>

            {/* Timesheet notice */}
            {!timesheetId && !callSid && !projectName && (
              <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-50 border border-amber-200">
                <span className="material-symbols-outlined text-base text-amber-600 shrink-0">info</span>
                <p className="text-xs text-amber-800">
                  Timesheet could not be auto-created (caller phone not found in project contacts).
                </p>
              </div>
            )}

            {/* Actions */}
            <div className="flex items-center gap-3 pt-1">
              <button
                type="button"
                onClick={handleConfirm}
                disabled={submitting}
                className="flex-1 py-3 rounded-xl bg-primary hover:bg-primary/90 text-on-primary font-bold text-sm flex items-center justify-center gap-2 shadow-sm transition-all disabled:opacity-60"
              >
                {submitting ? (
                  <>
                    <span className="material-symbols-outlined text-base animate-spin">progress_activity</span>
                    Submitting...
                  </>
                ) : (
                  <>
                    <span className="material-symbols-outlined text-base">send</span>
                    Confirm &amp; Submit to Manager
                  </>
                )}
              </button>
              <button
                type="button"
                onClick={onClose}
                disabled={submitting}
                className="px-5 py-3 rounded-xl border border-outline-variant/40 text-sm font-medium text-on-surface hover:bg-surface-container transition-colors"
              >
                Skip
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
